import { Box, TableRow, Tooltip } from '@mui/material';
import PropTypes from 'prop-types';
import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { capitalizar, obtenerColorResultado, ordenarLista } from '../../utils/UtilesComun';
import {
  ButtonTableApp,
  estiloFilaClasificacion,
  TableCellApp,
  TableCellDiferenciaGoles,
  TableModuleApp,
  TypographyTableApp,
} from '../custom/CustomComponents';

const columnsClasificacion = (embedded = false) => {
  // Embedded: Para contextos donde la tabla es estrecha (ej. ficha de equipo)
  const cols = [
    { field: 'posicion', label: '', width: embedded ? '8%' : '5%', sortable: false },
    { field: 'nombreEquipo', label: 'Equipo', width: embedded ? '44%' : '41%' },
    {
      field: 'puntos',
      label: 'P',
      width: embedded ? '9%' : '6%',
      align: 'center',
      tooltip: 'Puntos',
      sx: { borderLeft: '1px solid #e2e8f0', textAlign: 'center', px: '2px !important' },
    },
    {
      field: 'jugados',
      label: 'J',
      width: embedded ? '9%' : '6%',
      align: 'center',
      tooltip: 'Partidos Jugados',
      sx: { borderLeft: '1px solid #e2e8f0', textAlign: 'center', px: '2px !important' },
    },
  ];

  if (!embedded) {
    cols.push(
      {
        field: 'victorias',
        label: 'V',
        width: '5%',
        align: 'center',
        tooltip: 'Victorias',
        sx: { textAlign: 'center', px: '2px !important' },
      },
      {
        field: 'empates',
        label: 'E',
        width: '5%',
        align: 'center',
        tooltip: 'Empates',
        sx: { textAlign: 'center', px: '2px !important' },
      },
      {
        field: 'derrotas',
        label: 'D',
        width: '5%',
        align: 'center',
        tooltip: 'Derrotas',
        sx: { textAlign: 'center', px: '2px !important' },
      }
    );
  }

  cols.push(
    {
      field: 'diferenciaGoles',
      label: 'DG',
      width: embedded ? '10%' : '7%',
      align: 'center',
      tooltip: 'Diferencia de Goles',
      sx: { textAlign: 'center', px: '2px !important' },
    },
    {
      field: 'racha',
      label: 'Racha',
      width: embedded ? '20%' : '20%',
      align: 'left',
      tooltip: 'Últimos 5 partidos (orden cronológico)',
      sortable: false,
      sx: { borderLeft: '1px solid #e2e8f0', textAlign: 'left', pl: '8px !important', pr: '10px !important' },
    }
  );

  cols.forEach((c) => {
    c.sortable = false;
  });

  return cols;
};

/**
 * Fila de la tabla de clasificación.
 */
const FilaClasificacion = ({ equipo, posicion, resaltado, columns }) => {
  const navigate = useNavigate();

  return (
    <TableRow key={equipo.idEquipo} style={estiloFilaClasificacion(resaltado)}>
      {columns.map((col) => {
        if (col.field === 'posicion') {
          const esPrimero = posicion === 1;
          return (
            <TableCellApp key={col.field} sx={{ pl: '8px !important', pr: '4px !important', textAlign: 'center' }}>
              <Box
                sx={{
                  width: 22,
                  height: 22,
                  minWidth: 22,
                  borderRadius: '5px',
                  backgroundColor: esPrimero ? '#ecfdf5' : resaltado ? '#fef08a' : '#f1f5f9',
                  color: esPrimero ? '#047857' : resaltado ? '#854d0e' : '#475569',
                  border: `1px solid ${esPrimero ? '#a7f3d0' : resaltado ? '#facc15' : '#e2e8f0'}`,
                  fontWeight: 700,
                  fontSize: '0.74rem',
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  lineHeight: 1,
                  boxShadow: esPrimero ? '0 1px 3px rgba(16, 185, 129, 0.2)' : 'none',
                }}
              >
                {posicion}
              </Box>
            </TableCellApp>
          );
        }

        if (col.field === 'nombreEquipo') {
          return (
            <TableCellApp key={col.field} sx={{ pl: '8px !important', pr: '4px !important' }}>
              <ButtonTableApp
                enLista={false}
                onClick={() => navigate(`/equipo/${equipo.idEquipo}`)}
                text={capitalizar(equipo.nombreEquipo)}
                tooltip={capitalizar(equipo.nombreEquipo)}
                sx={{ px: 0.5, '& .MuiTypography-root': { fontSize: '0.8rem' } }}
              />
            </TableCellApp>
          );
        }

        if (col.field === 'puntos') {
          return (
            <TypographyTableApp
              key={col.field}
              align="center"
              sx={{
                borderLeft: '1px solid #e2e8f0',
                px: '2px !important',
                fontSize: '0.78rem',
                fontWeight: 700,
                textAlign: 'center',
              }}
            >
              {equipo.puntos}
            </TypographyTableApp>
          );
        }

        if (col.field === 'jugados') {
          return (
            <TypographyTableApp
              key={col.field}
              align="center"
              sx={{
                borderLeft: '1px solid #e2e8f0',
                px: '2px !important',
                fontSize: '0.75rem',
                textAlign: 'center',
              }}
            >
              {equipo.jugados}
            </TypographyTableApp>
          );
        }

        if (col.field === 'racha') {
          const racha = equipo.racha || [];
          return (
            <TableCellApp
              key={col.field}
              sx={{ borderLeft: '1px solid #e2e8f0', pl: '8px !important', pr: '10px !important' }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: '3px', justifyContent: 'flex-start' }}>
                {racha.length > 0 ? (
                  racha.map((p, idx) => {
                    const { bg, color, border, label: labelResultado } = obtenerColorResultado(p.resultado);

                    const tooltipText = p.jornada
                      ? `J${p.jornada}: ${labelResultado} ${p.golesLocal ?? '-'}-${p.golesVisitante ?? '-'} vs ${capitalizar(p.rival || '')} (${p.esLocal ? 'Casa' : 'Fuera'})`
                      : labelResultado;

                    return (
                      <Tooltip key={p.idPartido || idx} title={tooltipText} arrow>
                        <Box
                          sx={{
                            width: 15,
                            height: 15,
                            minWidth: 15,
                            borderRadius: '3px',
                            backgroundColor: bg,
                            border: `1px solid ${border}`,
                            color: color,
                            fontWeight: 700,
                            fontSize: '0.62rem',
                            display: 'inline-flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            lineHeight: 1,
                            cursor: 'default',
                            transition: 'all 0.12s ease-in-out',
                            '&:hover': {
                              transform: 'scale(1.18)',
                              boxShadow: '0 1px 3px rgba(0, 0, 0, 0.12)',
                            },
                          }}
                        >
                          {p.resultado}
                        </Box>
                      </Tooltip>
                    );
                  })
                ) : (
                  <Box sx={{ color: 'text.disabled', fontSize: '0.75rem' }}>-</Box>
                )}
              </Box>
            </TableCellApp>
          );
        }

        if (col.field === 'diferenciaGoles') {
          const dg = (equipo.golesFavor || 0) - (equipo.golesContra || 0);
          const signo = dg > 0 ? '+' : '';
          return (
            <TableCellDiferenciaGoles
              key={col.field}
              align="center"
              dg={dg}
              sx={{ px: '2px !important', fontSize: '0.7rem' }}
            >
              {signo}
              {dg}
            </TableCellDiferenciaGoles>
          );
        }

        return (
          <TypographyTableApp
            key={col.field}
            align="center"
            sx={{ px: '2px !important', fontSize: '0.75rem', textAlign: 'center' }}
          >
            {equipo[col.field]}
          </TypographyTableApp>
        );
      })}
    </TableRow>
  );
};

const Clasificacion = ({
  clasificacion,
  idEquipoResaltado,
  embedded = false,
  title = 'Clasificación',
  loading = false,
}) => {
  const [sort, setSort] = useState({ field: 'puntos', direction: 'desc' });
  const columns = useMemo(() => columnsClasificacion(embedded), [embedded]);

  const sortedClasificacion = useMemo(() => {
    return clasificacion ? ordenarLista(clasificacion, sort) : [];
  }, [clasificacion, sort]);

  if (!clasificacion && !loading) {
    return null;
  }

  return (
    <TableModuleApp
      title={title}
      columns={columns}
      sort={sort}
      setSort={setSort}
      loading={loading}
      data={sortedClasificacion}
      sx={{
        '& .MuiTableCell-head': {
          fontSize: '0.65rem',
        },
      }}
      renderRow={(equipo) => {
        const posicionReal = clasificacion ? clasificacion.findIndex((e) => e.idEquipo === equipo.idEquipo) + 1 : 0;
        return (
          <FilaClasificacion
            posicion={posicionReal}
            equipo={equipo}
            key={equipo.idEquipo}
            resaltado={Boolean(idEquipoResaltado) && Number(idEquipoResaltado) === Number(equipo.idEquipo)}
            columns={columns}
          />
        );
      }}
    />
  );
};

Clasificacion.propTypes = {
  clasificacion: PropTypes.array,
  idEquipoResaltado: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  embedded: PropTypes.bool,
  title: PropTypes.node,
  loading: PropTypes.bool,
};

FilaClasificacion.propTypes = {
  equipo: PropTypes.object,
  posicion: PropTypes.number,
  resaltado: PropTypes.bool,
  columns: PropTypes.array,
};

export default Clasificacion;
